'use client';
/* global google */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, TextInput, Button, Grid, Text, Select, Loader } from '@mantine/core';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMapsLibrary,
  MapMouseEvent,
} from '@vis.gl/react-google-maps';
import { useSpring, animated } from '@react-spring/web';
import { useTranslations } from 'next-intl';
import debounce from 'lodash/debounce';

export interface LocationData {
  formattedAddress: string;
  coordinates: { lat: number; lng: number };
  detailedAddress: {
    street: string;
    apartment: string;
    emirate: string;
  };
}

interface MapContainerProps {
  location: LocationData | null;
  setLocation: (location: LocationData) => void;
  onLocationChange?: (newLocation: LocationData) => void;
  initialCenter?: { lat: number; lng: number };
  defaultZoom?: number;
  countriesRestriction?: string[];
}

const DEFAULT_CENTER = { lat: 25.197197, lng: 55.274376 }; // Burj Khalifa coordinates
const DEFAULT_ZOOM = 18;

const emirates = [
  'Abu Dhabi',
  'Dubai',
  'Sharjah',
  'Ajman',
  'Umm Al Quwain',
  'Ras Al Khaimah',
  'Fujairah',
];

const AnimatedAdvancedMarker = animated(AdvancedMarker);

const MapContent: React.FC<MapContainerProps> = ({
  location,
  setLocation,
  onLocationChange,
  initialCenter = DEFAULT_CENTER,
  defaultZoom = DEFAULT_ZOOM,
  countriesRestriction = ['AE'],
}) => {
  const t = useTranslations('mapContainer');
  const [center, setCenter] = useState(initialCenter);
  const [detailedAddress, setDetailedAddress] = useState({
    street: '',
    apartment: '',
    emirate: 'Dubai',
  });
  const [currentPosition, setCurrentPosition] = useState<google.maps.LatLngLiteral>(initialCenter);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationAttempts, setLocationAttempts] = useState(0);

  const placesLibrary = useMapsLibrary('places');
  const geocodingLibrary = useMapsLibrary('geocoding');
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const [springs, api] = useSpring(() => ({
    from: { lat: initialCenter.lat, lng: initialCenter.lng },
  }));

  const debouncedGeocoding = useMemo(
    () =>
      debounce((pos: google.maps.LatLngLiteral) => {
        if (geocodingLibrary) {
          const geocoder = new geocodingLibrary.Geocoder();
          geocoder.geocode({ location: pos }, (results, status) => {
            setIsLoading(false);
            if (status === 'OK' && results && results[0]) {
              const addressComponents = results[0].address_components;
              let street = '';
              let emirate = '';

              addressComponents.forEach((component) => {
                if (component.types.includes('route')) {
                  street = component.long_name;
                }
                if (component.types.includes('administrative_area_level_1')) {
                  emirate = component.long_name;
                }
              });

              const newDetailedAddress = {
                street,
                apartment: detailedAddress.apartment,
                emirate,
              };

              const newLocation: LocationData = {
                formattedAddress: results[0].formatted_address,
                coordinates: { lat: pos.lat, lng: pos.lng },
                detailedAddress: newDetailedAddress,
              };

              setDetailedAddress(newDetailedAddress);
              setLocation(newLocation);
              onLocationChange?.(newLocation);
              setLocationAttempts(0);
            } else {
              handleLocationError();
            }
          });
        } else {
          setIsLoading(false);
          handleLocationError();
        }
      }, 300),
    [geocodingLibrary, setLocation, onLocationChange, detailedAddress.apartment]
  );

  useEffect(() => {
    if (placesLibrary && inputRef.current) {
      autocompleteRef.current = new placesLibrary.Autocomplete(inputRef.current, {
        componentRestrictions: { country: countriesRestriction },
        fields: ['address_components', 'geometry', 'name'],
        types: ['geocode'],
      });

      autocompleteRef.current.addListener('place_changed', handlePlaceSelect);
    }

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [placesLibrary, countriesRestriction]);

  useEffect(() => {
    updateLocationDetails(initialCenter);
    handleShowCurrentLocation();
  }, []);

  const updateLocationDetails = useCallback(
    (pos: google.maps.LatLngLiteral) => {
      setIsLoading(true);
      setCenter(pos);
      setCurrentPosition(pos);

      api.start({
        to: { lat: pos.lat, lng: pos.lng },
        config: { duration: 1000 },
      });

      debouncedGeocoding(pos);
    },
    [api, debouncedGeocoding]
  );

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current?.getPlace();
    if (place && place.geometry && place.geometry.location) {
      const pos = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      updateLocationDetails(pos);

      let street = '';
      let emirate = '';
      place.address_components?.forEach((component) => {
        if (component.types.includes('route')) {
          street = component.long_name;
        }
        if (component.types.includes('administrative_area_level_1')) {
          emirate = component.long_name;
        }
      });

      const newDetailedAddress = {
        street,
        apartment: '',
        emirate,
      };

      const newLocation: LocationData = {
        formattedAddress: place.formatted_address || '',
        coordinates: pos,
        detailedAddress: newDetailedAddress,
      };

      setDetailedAddress(newDetailedAddress);
      setLocation(newLocation);
      onLocationChange?.(newLocation);
      setLocationAttempts(0);
    }
  };

  const handleMarkerDrag = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        const newPos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        updateLocationDetails(newPos);
      }
    },
    [updateLocationDetails]
  );

  const handleMapClick = useCallback(
    (e: MapMouseEvent) => {
      if (e.detail.latLng) {
        const newPos = { lat: e.detail.latLng.lat, lng: e.detail.latLng.lng };
        updateLocationDetails(newPos);
      }
    },
    [updateLocationDetails]
  );

  const handleDetailedAddressChange =
    (field: keyof typeof detailedAddress) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const newDetailedAddress = { ...detailedAddress, [field]: event.target.value };
      setDetailedAddress(newDetailedAddress);
      if (location) {
        const newLocation: LocationData = {
          ...location,
          detailedAddress: newDetailedAddress,
        };
        setLocation(newLocation);
        onLocationChange?.(newLocation);
      }
    };

  const handleEmirateChange = (value: string | null) => {
    const newDetailedAddress = { ...detailedAddress, emirate: value || '' };
    setDetailedAddress(newDetailedAddress);
    if (location) {
      const newLocation: LocationData = {
        ...location,
        detailedAddress: newDetailedAddress,
      };
      setLocation(newLocation);
      onLocationChange?.(newLocation);
    }
  };

  const handleLocationError = () => {
    setLocationAttempts((prev) => prev + 1);
    if (locationAttempts >= 3) {
      setError(t('locationError'));
    }
  };

  const handleShowCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          updateLocationDetails(pos);
          setLocationAttempts(0);
        },
        () => {
          handleLocationError();
        },
        { timeout: 5000, maximumAge: 0 }
      );
    } else {
      handleLocationError();
    }
  };

  const springCenter: google.maps.LatLngLiteral = {
    lat: springs.lat.get(),
    lng: springs.lng.get(),
  };

  return (
    <Box>
      <Grid mb="md">
        <Grid.Col span={12}>
          <TextInput
            ref={inputRef}
            placeholder={t('searchPlaceholder')}
            size="md"
            rightSection={isLoading ? <Loader size="sm" /> : null}
          />
        </Grid.Col>
      </Grid>
      {error && locationAttempts >= 3 && (
        <Text color="red" mb="md">
          {error}
        </Text>
      )}
      <Grid mb="md">
        <Grid.Col span={4}>
          <TextInput
            placeholder={t('streetPlaceholder')}
            value={detailedAddress.street}
            onChange={handleDetailedAddressChange('street')}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            placeholder={t('apartmentPlaceholder')}
            value={detailedAddress.apartment}
            onChange={handleDetailedAddressChange('apartment')}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <Select
            placeholder={t('emiratePlaceholder')}
            data={emirates}
            value={detailedAddress.emirate}
            onChange={handleEmirateChange}
          />
        </Grid.Col>
      </Grid>
      <Button onClick={handleShowCurrentLocation} mb="md">
        {t('currentLocationButton')}
      </Button>
      <Box style={{ height: '600px', width: '100%' }}>
        <Map
          center={springCenter}
          zoom={defaultZoom}
          gestureHandling={'greedy'}
          disableDefaultUI={false}
          mapId={process.env.NEXT_PUBLIC_GOOGLE_MAPS_ID}
          onClick={handleMapClick}
        >
          {currentPosition && (
            <AnimatedAdvancedMarker
              position={springCenter}
              draggable
              onDragEnd={handleMarkerDrag}
            />
          )}
        </Map>
      </Box>
    </Box>
  );
};

const MapContainer: React.FC<MapContainerProps> = (props) => (
  <APIProvider apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || ''}>
    <MapContent {...props} />
  </APIProvider>
);

export default MapContainer;
